import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import {
    LayoutDashboard, Users, UserPlus, Route as RouteIcon, Truck, Receipt,
    Boxes, MessageSquare, BarChart3, Wrench, Settings, Sun, Moon,
    Lightbulb, LogOut, Radio, ShieldCheck, Smartphone, Code2, AlertTriangle, CalendarDays, History, DollarSign, TrendingUp, Map, UserCog, Mail, Clock as ClockIcon,
    Footprints, RotateCw, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppVersionBadge } from "@/components/AppVersionBadge";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard" },
    { to: "/customers", label: "Customers", icon: Users, testid: "sidebar-nav-customers" },
    { to: "/leads", label: "Signup Leads", icon: UserPlus, testid: "sidebar-nav-leads" },
    { to: "/service-area", label: "Service Area", icon: Map, testid: "sidebar-nav-service-area" },
    { to: "/users", label: "Team & Drivers", icon: UserCog, testid: "sidebar-nav-users" },
    { to: "/security", label: "Security Activity", icon: ShieldCheck, testid: "sidebar-nav-security", indent: true, adminOnly: true },
    { to: "/timesheets", label: "Timesheets", icon: ClockIcon, testid: "sidebar-nav-timesheets" },
    { to: "/routing", label: "Routing", icon: RouteIcon, testid: "sidebar-nav-routing" },
    { to: "/sequencing", label: "Driver Sequencing", icon: Footprints, testid: "sidebar-nav-sequencing", indent: true },
    { to: "/rolloff", label: "Roll-Off Dispatch", icon: Truck, testid: "sidebar-nav-rolloff" },
    { to: "/dispatch", label: "Dispatch Center", icon: Radio, testid: "sidebar-nav-dispatch" },
    { to: "/driver", label: "Driver App", icon: Smartphone, testid: "sidebar-nav-driver" },
    { to: "/holidays", label: "Holidays", icon: CalendarDays, testid: "sidebar-nav-holidays" },
    { to: "/billing", label: "Billing", icon: Receipt, testid: "sidebar-nav-billing" },
    { to: "/payment-recovery", label: "Failed Payments", icon: RotateCw, testid: "sidebar-nav-payment-recovery", indent: true },
    { to: "/ar-aging", label: "A/R Aging", icon: AlertTriangle, testid: "sidebar-nav-ar-aging" },
    { to: "/expenses", label: "Expenses", icon: DollarSign, testid: "sidebar-nav-expenses" },
    { to: "/profitability", label: "Profitability", icon: TrendingUp, testid: "sidebar-nav-profitability" },
    { to: "/fleet", label: "Fleet", icon: Wrench, testid: "sidebar-nav-fleet" },
    { to: "/carts", label: "Carts", icon: Package, testid: "sidebar-nav-carts" },
    { to: "/inventory", label: "Inventory", icon: Boxes, testid: "sidebar-nav-inventory" },
    { to: "/communications", label: "Communications", icon: MessageSquare, testid: "sidebar-nav-communications" },
    { to: "/communications/templates", label: "Templates", icon: Mail, testid: "sidebar-nav-templates", indent: true },
    { to: "/reports", label: "Reports", icon: BarChart3, testid: "sidebar-nav-reports" },
    { to: "/reports/sales-tax", label: "Sales Tax", icon: Receipt, testid: "sidebar-nav-sales-tax", indent: true },
    { to: "/feature-requests", label: "Feature Requests", icon: Lightbulb, testid: "sidebar-nav-feature-requests" },
    { to: "/audit", label: "Audit Log", icon: History, testid: "sidebar-nav-audit" },
    { to: "/embed", label: "Website Embed", icon: Code2, testid: "sidebar-nav-embed" },
    { to: "/settings", label: "Settings", icon: Settings, testid: "sidebar-nav-settings" },
];

export default function Sidebar() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const nav = useNavigate();

    const handleLogout = async () => {
        await logout();
        nav("/login");
    };

    return (
        <aside className="w-64 shrink-0 h-screen sticky top-0 border-r border-border surface flex flex-col"
               data-testid="app-sidebar">
            <div className="px-5 py-5 border-b border-border flex items-center gap-3">
                <div className="bg-white rounded-md px-2.5 py-1.5 w-full" data-testid="sidebar-brand-logo">
                    <img src="/wastehq-logo.webp" alt="WasteHQ" className="h-8 w-auto" />
                </div>
            </div>

            <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
                {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.to === "/"}
                        data-testid={n.testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                                n.indent ? "ml-6 text-xs" : ""
                            } ${
                                isActive
                                    ? "bg-secondary text-foreground border-l-2 border-primary -ml-[2px] pl-[14px]"
                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                            }`
                        }
                    >
                        <n.icon className={n.indent ? "w-[14px] h-[14px] shrink-0" : "w-[18px] h-[18px] shrink-0"} />
                        <span className="truncate">{n.label}</span>
                    </NavLink>
                ))}
            </nav>

            <div className="border-t border-border p-3 space-y-2">
                <div className="flex items-center gap-3 px-2 py-1.5">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs">
                        {user?.name?.slice(0, 2)?.toUpperCase() || "RD"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{user?.name}</div>
                        <div className="overline text-[10px] mt-0.5">{user?.role}</div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={toggle} className="flex-1"
                            data-testid="theme-toggle">
                        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleLogout} className="flex-1"
                            data-testid="logout-btn">
                        <LogOut className="w-4 h-4" />
                    </Button>
                </div>
                <div className="pt-3 flex justify-center border-t border-border/50 mt-2">
                    <AppVersionBadge />
                </div>
            </div>
        </aside>
    );
}
